import React, {useEffect, useMemo} from "react";
import {UseFormGetValues, useWatch} from "react-hook-form";
import {isEqual} from "lodash";
import {useThrottled} from "../../hooks/useThrottled.ts";
import {
    DerivedComponent,
    FormatAs,
    FormComponentProps,
    FormType,
    FormValues,
    KeyGridMeta,
    RuleType,
} from "../../DynamicFormTypes.ts";
import {useFormMethods} from "../../DyanmicFormContext.tsx";
import {StaticField} from "../staticfield/StaticField.tsx";
import {roundToNearest, safeParseDate, safeParseValue} from "./derivedUtils.ts";
import {extractDataGridKey} from "../../dyanimcFormUtil.tsx";

/**
    * A read-only field that calculates its value based on other fields according to a specified rule.
 */


const calculateValue = (component: DerivedComponent, getValues: UseFormGetValues<FormValues>): any => {
    const {listFieldKeys} = component;

    switch (component.calculationRule) {
        case RuleType.SUM_ALL: {
            let sum = 0;
            listFieldKeys.forEach(({dataGrid, fieldKey}) => {
                if (dataGrid) {
                    const grid: any[] = getValues(dataGrid) ?? [];
                    grid.forEach(row => {
                        const parsed = safeParseValue(row?.[fieldKey]);
                        if (parsed) sum += parsed;
                    });
                } else {
                    const parsed = safeParseValue(getValues(fieldKey));
                    if (parsed) sum += parsed;
                }
            });
            return sum;
        }

        case RuleType.SUM_RECOMMENDED_REPAIRS: {
            const repairCostKey = listFieldKeys.find(x => x.meta === KeyGridMeta.REPAIR_COST)?.fieldKey;
            const recommendedKey = listFieldKeys.find(x => x.meta === KeyGridMeta.RECOMMENDED)?.fieldKey;
            const dataGridKey = listFieldKeys.find(x => x.dataGrid)?.dataGrid;
            if (!repairCostKey || !recommendedKey || !dataGridKey) return 0;

            const grid: any[] = getValues(dataGridKey) ?? [];
            let sum = 0;
            grid.forEach(row => {
                if (row?.[recommendedKey] === "yes") {
                    const cost = safeParseValue(row?.[repairCostKey]);
                    if (cost) sum += cost;
                }
            });
            return sum;
        }

        case RuleType.SUM_ONE: {
            const fullKey = extractDataGridKey(component.fieldKey);
            const myRow = fullKey.row!;
            const nestedKeys = listFieldKeys.filter(f => f.dataGrid).map(f => f.fieldKey);
            const dataGridKeys = [...new Set(listFieldKeys.filter(f => f.dataGrid).map(f => f.dataGrid!))];
            let sum = 0;
            dataGridKeys.forEach(dg => {
                const row = (getValues(dg) ?? [])[myRow];
                nestedKeys.forEach(key => {
                    const parsed = safeParseValue(row?.[key]);
                    if (parsed) sum += parsed;
                });
            });
            return Math.round(sum);
        }

        case RuleType.SUM_ABS_ONE: {
            const fullKey = extractDataGridKey(component.fieldKey);
            const myRow = fullKey.row!;
            const nestedKeys = listFieldKeys.filter(f => f.dataGrid).map(f => f.fieldKey);
            const dataGridKeys = [...new Set(listFieldKeys.filter(f => f.dataGrid).map(f => f.dataGrid!))];
            let sum = 0;
            dataGridKeys.forEach(dg => {
                const row = (getValues(dg) ?? [])[myRow];
                nestedKeys.forEach(key => {
                    const parsed = safeParseValue(row?.[key]);
                    if (parsed) sum += Math.abs(parsed);
                });
            });
            return sum;
        }

        case RuleType.PERCENT_OF: {
            const fullKey = extractDataGridKey(component.fieldKey);
            const myRow = fullKey.row!;
            const numeratorKey = listFieldKeys.find(x => x.meta === KeyGridMeta.NUMERATOR);
            const denominatorKey = listFieldKeys.find(x => x.meta === KeyGridMeta.DENOMINATOR);
            if (!numeratorKey || !denominatorKey) {
                console.error("PERCENT_OF: missing NUMERATOR or DENOMINATOR meta", component);
                return 0;
            }
            const row = (getValues(numeratorKey.dataGrid!) ?? [])[myRow];
            const n = safeParseValue(row?.[numeratorKey.fieldKey]) ?? 0;
            const d = safeParseValue(row?.[denominatorKey.fieldKey]) ?? 0;
            return n && d ? Math.round((n / d) * 1000) / 10 : 0;
        }

        case RuleType.LOWEST:
        case RuleType.HIGHEST: {
            const prices: number[] = [];
            listFieldKeys.forEach(({dataGrid, fieldKey}) => {
                if (dataGrid) {
                    const grid: any[] = getValues(dataGrid) ?? [];
                    grid.forEach(row => {
                        const parsed = safeParseValue(row?.[fieldKey]);
                        if (parsed !== undefined) prices.push(parsed);
                    });
                } else {
                    const parsed = safeParseValue(getValues(fieldKey));
                    if (parsed !== undefined) prices.push(parsed);
                }
            });
            const fn = component.calculationRule === RuleType.HIGHEST ? Math.max : Math.min;
            return prices.length ? fn(...prices) : 0;
        }

        case RuleType.MOST_RECENT: {
            const priceKey = listFieldKeys.find(x => x.meta === KeyGridMeta.PRICE)?.fieldKey;
            const dateKey = listFieldKeys.find(x => x.meta === KeyGridMeta.DATE)?.fieldKey;
            const dataGridKey = listFieldKeys.find(x => x.dataGrid)?.dataGrid;
            if (!priceKey || !dateKey || !dataGridKey) return {newestDate: undefined, newestPrice: 0};

            const grid: any[] = getValues(dataGridKey) ?? [];
            let newestDate: Date | undefined;
            let newestPrice = 0;
            grid.forEach(row => {
                if (row?.[dateKey] && row?.[priceKey]) {
                    const parsedDate = safeParseDate(row[dateKey]);
                    if (parsedDate?.getTime() && (!newestDate || parsedDate > newestDate)) {
                        newestDate = parsedDate;
                        newestPrice = row[priceKey];
                    }
                }
            });
            return {newestDate, newestPrice};
        }

        case RuleType.MULTIPLY_PERCENT: {
            const percentageKey = listFieldKeys.find(x => x.meta === KeyGridMeta.PERCENTAGE)?.fieldKey;
            const numberKey = listFieldKeys.find(x => x.meta === KeyGridMeta.NUMBER)?.fieldKey;
            const percentage = safeParseValue(getValues(percentageKey!));
            const value = safeParseValue(getValues(numberKey!));
            if (percentage && value) return roundToNearest(value + value * percentage / 100, 500);
            return component.defaultValue?.value ?? "-";
        }

        default:
            return undefined;
    }
};

const ReadOnlyCalculationRenderer: React.FC<FormComponentProps<DerivedComponent>> = ({component}) => {
    const {formMethods} = useFormMethods();
    const {control, getValues, setValue} = formMethods;

    const watchKeys = useMemo(() => [
        ...new Set(component.listFieldKeys.filter(f => f.dataGrid).map(f => f.dataGrid!)),
        ...component.listFieldKeys.filter(f => !f.dataGrid).map(f => f.fieldKey),
    ], [component.listFieldKeys]);

    const trigger = useWatch({control, name: watchKeys});
    const throttledTrigger = useThrottled(trigger, 150);

    const calculatedValue = useMemo(
        () => calculateValue(component, getValues),
        [throttledTrigger]
    );

    // Write the calculated value back into the form so downstream getValues() calls see it
    useEffect(() => {
        if (isEqual(getValues(component.fieldKey), calculatedValue)) return;
        setValue(component.fieldKey, calculatedValue, {shouldDirty: false, shouldTouch: false});
    }, [calculatedValue]);

    const rule = component.calculationRule;

    if (rule === RuleType.MOST_RECENT) {
        return <>
            <StaticField registerWithForm={false} component={{type: FormType.STATIC_FIELD, formatAs: FormatAs.CURRENCY, fieldKey: component.fieldKey, label: `${component.label} Price`, compactUI: component.compactUI, placeHolder: component.defaultValue, value: calculatedValue?.newestPrice, width: 6, validationRules: component.validationRules, suffix: component.suffix}}/>
            <StaticField registerWithForm={false} component={{type: FormType.STATIC_FIELD, formatAs: FormatAs.DATE, fieldKey: component.fieldKey, label: `${component.label} Date`, compactUI: component.compactUI, placeHolder: component.defaultValue, value: calculatedValue?.newestDate, width: 6, validationRules: component.validationRules, suffix: component.suffix}}/>
        </>;
    }

    if (rule === RuleType.PERCENT_OF) {
        return <StaticField registerWithForm={false} component={{type: FormType.STATIC_FIELD, formatAs: FormatAs.PERCENTAGE, fieldKey: component.fieldKey, label: component.label, compactUI: component.compactUI, placeHolder: component.defaultValue, value: calculatedValue, validationRules: component.validationRules, suffix: component.suffix}}/>;
    }

    return <StaticField registerWithForm={false} component={{type: FormType.STATIC_FIELD, formatAs: FormatAs.CURRENCY, fieldKey: component.fieldKey, label: component.label, compactUI: component.compactUI, placeHolder: component.defaultValue, value: calculatedValue, validationRules: component.validationRules, suffix: component.suffix}}/>;
};

export const ReadOnlyCalculation = React.memo(ReadOnlyCalculationRenderer)